/**
 * API Services – Facade (re-exports only)
 *
 * Do not add new functions here. Import from domain services instead:
 * - authService: loginUser, logout, getCurrentUser, refreshToken
 * - organizationService: getOrganizationById, getAllOrganizations, createOrganization, ...
 * - facilitiesService: getFacilitiesByOrganization, getFacilities, createFacility, ...
 * - locationsService: getLocationsByFacility, createLocation, ...
 * - userService: getUsers, getUsersByOrganization, updateUserApi
 * - userInviteService: createInvitation, acceptInvitation, inviteUser, ...
 * - onboardingService: getOnboardingStatus, getWizardStatus, completeWizardStep
 * - addressService, deviceDataService, deviceEventsService, waterChangeStandardService, beaconService
 */

import { apiClient } from './apiClient';
import { serviceLogger } from '../utils/serviceLogger';

// Re-export for consumers that import refreshToken from apiServices (e.g. authThunks, AuthContext)
export { refreshToken } from './apiClient';

// --- Auth ---
export { loginUser, logout, getCurrentUser, getCurrentUserFromApi } from './authService';

// --- Organization ---
export {
  getOrganizationById,
  getAllOrganizations,
  addOrganization,
  updateOrganization,
  updateOrganizationV2,
  deleteOrganization,
  createOrganization,
  createAdministrator,
} from './organizationService';

// --- Facilities ---
export {
  getFacilitiesByOrganization,
  getAllFacilities,
  getDataUnitsByFacility,
  associateDeviceIdentifier,
  disassociateDeviceIdentifier,
} from './facilitiesService';
export { createFacility as addFacility, updateFacility, deleteFacility } from './facilitiesService';

// --- Locations ---
export { getLocationsByFacility } from './locationsService';

// --- Users ---
export { getUsers, updateUserApi as updateUser } from './userService';

// --- Invitations ---
export { createInvitation, acceptInvitation } from './userInviteService';

// --- Address ---
export {
  addAddress,
  updateAddress,
  deleteAddress,
  getAllAddresses,
  getAllAddressesByFacId,
} from './addressService';

// --- Device data ---
export {
  getBatteryHistory,
  getUsageHistory,
  getLocationHistory,
  getWaterChangesHistory,
  getFilterLife,
  getLocationsVisitedLastDay,
} from './deviceDataService';

// --- Device events ---
export {
  getDeviceEvents,
  getWaterEvents,
  getDeviceEvent,
  getWaterEvent,
  getLatestUnitDayDuration,
  getTotalUsageTimeToday,
} from './deviceEventsService';

// --- Water change standard ---
export {
  getWaterChangeStandardByFacility,
  setWaterChangeStandardForFacilities,
} from './waterChangeStandardService';

// --- Beacon (facility_locations) ---
export {
  associateBeaconToLocation,
  getBeaconAssociations,
  updateBeaconAssociation,
  deleteBeaconAssociation,
} from './beaconService';

// --- Onboarding / wizard ---
export { getWizardStatus, completeWizardStep } from './onboardingService';

/**
 * Get onboarding status in legacy format (steps.facility_locations, team_roles, cleaning_standards).
 * @deprecated Use getOnboardingStatus from onboardingService instead.
 */
export const getOnboardingOrganizationStatus = async (organizationId) => {
  const { getOnboardingStatus } = await import('./onboardingService');
  try {
    const response = await getOnboardingStatus();
    if (response?.data?.steps) {
      const transformedSteps = {};
      if (response.data.steps.step1_organization_structure) {
        transformedSteps.facility_locations =
          response.data.steps.step1_organization_structure.completed;
      }
      transformedSteps.team_roles = false;
      if (response.data.steps.step3_cleaning_standards) {
        transformedSteps.cleaning_standards =
          response.data.steps.step3_cleaning_standards.completed;
      }
      return {
        steps: transformedSteps,
        isComplete: response.data.isComplete,
        canAccessDashboard: response.data.canAccessDashboard,
      };
    }
    return {
      steps: { facility_locations: false, team_roles: false, cleaning_standards: false },
      isComplete: false,
      canAccessDashboard: false,
    };
  } catch (error) {
    serviceLogger.error(
      `Error fetching onboarding status for organization ${organizationId}:`,
      error,
    );
    throw error;
  }
};

/** Summary version 2 */

// Gets locations visited the last day with activity
export const getDeviceMetrics = async (deviceId, params) => {
  try {
    const response = await apiClient.get(`/devices/${deviceId}/metrics/`, {
      params: {
        ...params,
      },
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching locations visited:', error);
    throw error;
  }
};

export const getFacilityMetricsSummary = async (deviceId, date) => {
  try {
    const response = await apiClient.get(`/devices/${deviceId}/summary`, {
      params: {
        groupBy: 'day',
        date: date,
      },
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching locations visited:', error);
    throw error;
  }
};
// authApi.js
// Ajustá la ruta base si tu backend vive bajo /web-api/... (acá parece root)

export const requestOtp = async (email) => {
  const res = await apiClient.get('/reset_password_otp/', {
    params: { email },
    withCredentials: true,
  });
  return res.data; // { detail: ... }
};

export const verifyOtp = async ({ email, otp }) => {
  const res = await apiClient.patch(
    '/reset_password_otp/',
    { email, otp },
    {
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      withCredentials: true,
    },
  );
  return res.data; // { detail, verified: true }
};

export const resetPasswordWithOtp = async ({ email, password, verifyPassword }) => {
  const res = await apiClient.post(
    '/reset_password_otp/',
    JSON.stringify({
      email,
      password,
      verify_password: verifyPassword,
    }),
    {
      headers: { 'Content-Type': 'application/json' },
      withCredentials: true,
    },
  );
  return res.data; // { detail: "Password reset successfully" }
};
